import { api, unwrap } from '@saas-kit/lib';

export const MEMBERSHIP_ROLES = ['owner', 'admin', 'manager', 'member', 'viewer'] as const;

export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

/** A role assigned to a membership (system base role or a custom tenant role). */
export interface RoleSummary {
  id: string;
  slug: string;
  name: string;
  is_system: boolean;
}

/** A tenant role definition as returned by GET /roles. */
export interface TenantRole extends RoleSummary {
  tenant_id: string;
  description: string | null;
  is_default: boolean;
}

export interface Member {
  id: string;
  tenant_id: string;
  user_id: string;
  role: MembershipRole;
  status: string;
  email: string;
  full_name: string | null;
  roles: RoleSummary[];
}

interface ListEnvelope<T> {
  items: T[];
}

export async function listMembers(): Promise<Member[]> {
  const data = await unwrap<ListEnvelope<Member>>(api.get('/memberships'));
  return data.items;
}

/** All roles defined for the tenant — system roles plus custom ones. */
export async function listTenantRoles(): Promise<TenantRole[]> {
  const data = await unwrap<ListEnvelope<TenantRole>>(api.get('/roles'));
  return data.items;
}

export async function changeMembershipRole(
  membershipId: string,
  role: MembershipRole,
): Promise<void> {
  await api.post(`/memberships/${membershipId}/role`, { role });
}

/** Grant a tenant role (system or custom) to a membership. */
export async function assignMembershipRole(
  membershipId: string,
  roleId: string,
): Promise<void> {
  await api.post(`/memberships/${membershipId}/roles`, { role_id: roleId });
}

/** Revoke a tenant role from a membership. */
export async function revokeMembershipRole(
  membershipId: string,
  roleId: string,
): Promise<void> {
  await api.delete(`/memberships/${membershipId}/roles/${roleId}`);
}

export async function suspendMembership(membershipId: string): Promise<void> {
  await api.post(`/memberships/${membershipId}/suspend`);
}

export async function removeMembership(membershipId: string): Promise<void> {
  await api.post(`/memberships/${membershipId}/remove`);
}

export async function reactivateMembership(membershipId: string): Promise<void> {
  await api.post(`/memberships/${membershipId}/reactivate`);
}
